/**
 * P2-12 主管一键开会议程 Modal
 *
 * 流程：
 *   1. 打开 → POST /teams/:id/generate-meeting-agenda 拉 AI 议程
 *   2. 展示 5 段议程（每段 talking_points + action_items）
 *   3. 点"发送钉钉" → POST /teams/:id/send-meeting-agenda
 */
import { useEffect, useState } from 'react';
import { Modal, message, Spin } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn } from './ui';

interface AgendaItem {
  section: string;
  minutes?: number;
  talking_points?: string[];
  action_items?: string[];
}
interface Agenda {
  title?: string;
  duration_minutes?: number;
  items: AgendaItem[];
}

export function MeetingAgendaModal({
  open, teamId, teamName, onClose,
}: {
  open: boolean;
  teamId: number;
  teamName: string;
  onClose: () => void;
}) {
  const [agenda, setAgenda] = useState<Agenda | null>(null);

  const gen = useMutation({
    mutationFn: () => $.post<Agenda>(`/teams/${teamId}/generate-meeting-agenda`, {}),
    onSuccess: (r) => setAgenda(r),
  });

  const send = useMutation({
    mutationFn: () => $.post(`/teams/${teamId}/send-meeting-agenda`, { agenda }),
    onSuccess: () => {
      message.success('会议议程已推送钉钉团队群');
      onClose();
    },
  });

  useEffect(() => {
    if (open && !agenda) gen.mutate();
    if (!open) setAgenda(null);
  }, [open]);

  return (
    <Modal
      title={<>📅 {teamName} · {agenda?.title ?? '会议议程'} <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>{agenda?.duration_minutes && `${agenda.duration_minutes} 分钟`}</span></>}
      open={open}
      width={780}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Btn kind="default" onClick={() => gen.mutate()} disabled={gen.isPending}>
            {gen.isPending ? '生成中…' : '🔄 换一份'}
          </Btn>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="default" onClick={onClose}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => send.mutate()}
                 disabled={!agenda || send.isPending}>
              {send.isPending ? '发送中…' : '📨 发送钉钉给团队'}
            </Btn>
          </div>
        </div>
      }
    >
      {gen.isPending && !agenda && (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin tip="AI 生成议程中（基于本周团队数据）…" />
        </div>
      )}
      {agenda && (
        <div style={{ maxHeight: 540, overflowY: 'auto' }}>
          {agenda.items.map((item, i) => (
            <div key={i} style={{
              marginBottom: 14, padding: 12,
              background: 'var(--bg-2)', borderRadius: 6,
              borderLeft: '3px solid var(--purple)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{
                  width: 22, height: 22, lineHeight: '22px', borderRadius: 11,
                  background: 'var(--purple)', color: '#fff',
                  fontSize: 11, fontWeight: 600, textAlign: 'center',
                }}>{i + 1}</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{item.section}</span>
                {item.minutes && (
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                    {item.minutes} 分钟
                  </span>
                )}
              </div>
              {(item.talking_points ?? []).map((t: string, ti: number) => (
                <div key={ti} style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 3, paddingLeft: 6 }}>
                  · {t}
                </div>
              ))}
              {item.action_items && item.action_items.length > 0 && (
                <div style={{
                  marginTop: 8, padding: '6px 10px',
                  background: 'rgba(250,140,22,0.08)', borderRadius: 4,
                }}>
                  <div style={{ fontSize: 11, color: 'var(--warning-text)', fontWeight: 500, marginBottom: 4 }}>
                    Action Items
                  </div>
                  {item.action_items.map((a: string, ai: number) => (
                    <div key={ai} style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 2 }}>
                      ☐ {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
